import React, { useEffect } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useCandidateT } from '../../i18n/candidate';

/**
 * Candidate view of the personality (IPIP-50) questionnaire: statements in pages of 10, each
 * answered on the five-point accuracy scale. Answers are stored as 1..5 through `onAnswer`.
 * The page is derived from `currentIndex` (the page's first statement) so the existing
 * session-storage draft restores the candidate to the right page.
 */
export const LIKERT_SCALE = [
  { value: 1, label: 'Very Inaccurate' },
  { value: 2, label: 'Moderately Inaccurate' },
  { value: 3, label: 'Neither Accurate Nor Inaccurate' },
  { value: 4, label: 'Moderately Accurate' },
  { value: 5, label: 'Very Accurate' },
];

export const LIKERT_PAGE_SIZE = 10;

const LikertQuestionnaire = ({ questions, answers, onAnswer, currentIndex, onGoToIndex, onFinish, isSubmitting, finishLabel }) => {
  const t = useCandidateT();
  const pageCount = Math.max(1, Math.ceil(questions.length / LIKERT_PAGE_SIZE));
  const page = Math.min(Math.floor((currentIndex || 0) / LIKERT_PAGE_SIZE), pageCount - 1);
  const start = page * LIKERT_PAGE_SIZE;
  const pageQuestions = questions.slice(start, start + LIKERT_PAGE_SIZE);
  const pageComplete = pageQuestions.every((q) => Number(answers[q._id]) >= 1);
  const answeredCount = questions.filter((q) => Number(answers[q._id]) >= 1).length;
  const isLastPage = page === pageCount - 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  return (
    <div className="bg-white rounded-xl shadow p-4 sm:p-6" data-testid="likert-questionnaire">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{t('Work Style Questionnaire')}</h2>
        <span className="text-xs text-gray-500 sm:text-sm">
          {t('Page {page} of {pages} · {answered}/{total} answered', {
            page: page + 1,
            pages: pageCount,
            answered: answeredCount,
            total: questions.length,
          })}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-4 sm:mb-6">
        {t('How accurately does each statement describe you? There are no right or wrong answers — answer as you honestly see yourself now, not as you wish to be.')}
      </p>

      <div className="hidden md:grid grid-cols-[minmax(0,1fr)_repeat(5,6.5rem)] gap-2 px-3 pb-2 text-[11px] font-medium text-gray-500 text-center">
        <span />
        {LIKERT_SCALE.map((s) => (
          <span key={s.value}>{t(s.label)}</span>
        ))}
      </div>

      <ol className="space-y-3" start={start + 1}>
        {pageQuestions.map((q, idx) => {
          const selected = Number(answers[q._id]) || null;
          return (
            <li
              key={q._id}
              data-testid={`likert-item-${start + idx + 1}`}
              className={`rounded-lg border px-3 py-3 md:grid md:grid-cols-[minmax(0,1fr)_repeat(5,6.5rem)] md:items-center md:gap-2 ${
                selected ? 'border-gray-200' : 'border-gray-200 bg-gray-50/60'
              }`}
            >
              <p className="text-sm text-gray-900 mb-2 md:mb-0">
                <span className="text-gray-400 mr-1.5">{start + idx + 1}.</span>
                {q.content || q.title}
              </p>
              <div className="grid grid-cols-5 gap-1.5 md:contents" role="radiogroup" aria-label={q.content || q.title}>
                {LIKERT_SCALE.map((s) => {
                  const isOn = selected === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      role="radio"
                      aria-checked={isOn}
                      title={t(s.label)}
                      data-testid={`likert-item-${start + idx + 1}-option-${s.value}`}
                      onClick={() => onAnswer(q._id, s.value)}
                      className={`rounded-md border px-1 py-2 text-[11px] leading-tight transition-colors md:text-xs ${
                        isOn
                          ? 'border-indigo-600 bg-indigo-600 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-400'
                      }`}
                    >
                      <span className="md:hidden">{t(s.label)}</span>
                      <span className="hidden md:inline">{s.value}</span>
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 gap-2 pb-36 sm:pb-0">
        <button
          type="button"
          onClick={() => onGoToIndex(Math.max(0, start - LIKERT_PAGE_SIZE))}
          disabled={page === 0}
          className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
          {t('Previous')}
        </button>
        {!pageComplete && <span className="text-xs text-gray-500">{t('Answer every statement on this page to continue.')}</span>}
        {isLastPage ? (
          <button
            type="button"
            data-testid="likert-finish-btn"
            onClick={onFinish}
            disabled={!pageComplete || isSubmitting}
            className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('Submitting...') : finishLabel}
          </button>
        ) : (
          <button
            type="button"
            data-testid="likert-next-btn"
            onClick={() => onGoToIndex(start + LIKERT_PAGE_SIZE)}
            disabled={!pageComplete}
            className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('Next')}
            <ArrowRightIcon className="h-4 w-4 ml-1.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LikertQuestionnaire;
